// ============================================================================
// ÄNDERUNGSPROTOKOLL - HiringCTASection.js
// ============================================================================
//
// DATUM: 25.11.2025
//
// ZWECK DER ÄNDERUNG:
// Der Hintergrund wurde von einem Gradient auf bg-brand-light-blue geändert,
// damit die Section nahtlos in die SolutionsSection darüber übergeht.
// Die SolutionsSection hat ebenfalls bg-brand-light-blue, und die Kurve
// dazwischen wurde ebenfalls auf #E4F5FF (brand-light-blue) geändert.
//
// AUSGANGSPUNKT (VORHER):
// - className: "bg-gradient-to-br from-blue-300 via-blue-200 to-blue-100"
// - Überlagernder radial-gradient mit blur-Effekt
//
// ENDPUNKT (NACHHER):
// - className: "bg-brand-light-blue" (#E4F5FF)
// - Radial-gradient beibehalten für visuellen Effekt
//
// VERKNÜPFUNG:
// - SolutionsSection.js: Kurve wurde auf #E4F5FF geändert
// - Beide Sections haben jetzt denselben Basis-Hintergrund
//
// ZUM ZURÜCKSETZEN:
// - className zurück auf: "bg-gradient-to-br from-blue-300 via-blue-200 to-blue-100"
// ============================================================================

import React from 'react';
import teamTalkImg from '../img/teamTalk.png';
import FadeInSection from './FadeInSection';
import FadeInImage from './FadeInImage';
import { useTranslation } from '../i18n/useTranslation';

const HiringCTASection = () => {
  const { t, locale } = useTranslation();
  return (
    <section
      className="relative overflow-hidden py-20 lg:py-32 bg-gradient-to-r from-blue-300 via-blue-200 to-blue-100"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <FadeInSection animation="fade-in-up" threshold={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-gray-800">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-8">
                {t('hiringCTA.title')}
                <br/>
                <span className="text-blue-700 mt-2 inline-block">{t('hiringCTA.subtitle')}</span>
              </h2>
              <a
                href={`/${locale}/unternehmen/career`}
                className="inline-block bg-white text-blue-600 hover:bg-gray-100 font-bold text-sm uppercase tracking-wider rounded-full px-8 py-4 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                {t('hiringCTA.ctaButton')}
              </a>
            </div>

            {/* Image */}
            <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <FadeInImage
                src={teamTalkImg}
                alt={t('hiringCTA.imageAlt')}
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
                width="800"
                height="400"
                showSkeleton={true}
              />
            </div>
            {/* Decorative shape overlay */}
            <div
              className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(147, 51, 234, 0.3) 100%)',
                filter: 'blur(60px)'
              }}
            />
          </div>
        </div>
      </FadeInSection>
      </div>
      
      {/* Curved transition to next section - matching other curves */}
      <div className="absolute bottom-0 left-0 w-full h-32 sm:h-36 md:h-40 lg:h-44 xl:h-48">
        <svg
          viewBox="0 0 3000 160"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          <path
            d="M0 160H3000V0C3000 0 2250 130 1500 130C750 130 0 0 0 0V160Z"
            fill="#20005E"
          />
        </svg>
      </div>
    </section>
  );
};

export default HiringCTASection;